import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Heading, Inscription, ListItemBase, Preloader, Responsive, Text } from '@common-fe/common-fe';

import ROUTES from '@/common/routes';
import AppButton from '@/components/controls/AppButton';
import { Transaction } from '@/modules/transaction/transaction.types';

import { useTransactionsList } from '../../hooks';
import RecentActivity from '../RecentActivity';
import { EmptyTransactionsListPlaceholder } from '../TransactionsList/EmptyTransactionsListPlaceholder';
import TransactionsListHeader from '../TransactionsListHeader/TransactionsListHeader';

import { Wrapper } from './RecentActivities.styles';

const BACKLIGHT_DELAY = 2000;
const QUERY_KEY = 'justAddedClaimId';

const headers: ListItemBase[] = [
  {
    key: 'provider',
    title: 'Provider',
    flex: 1.5,
  },
  {
    key: 'requestedAmount',
    title: 'Requested amount',
    flex: 1,
  },
  {
    key: 'paidAmount',
    title: 'Paid amount',
    flex: 0.8,
  },
  {
    key: 'status',
    title: 'Status',
    flex: 1.2,
  },
  {
    key: 'statusDate',
    title: 'Status date',
  },
];

const RecentActivities = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const { push } = useHistory();
  const { search, pathname } = useLocation();
  const justAddedClaimId = useMemo(() => new URLSearchParams(search).get(QUERY_KEY), [search]);
  const { data, total, isSuccess, isLoading } = useTransactionsList({
    size: 5,
  });
  const handleOpenList = () => {
    history.push(ROUTES.EXPENSE_DETAILS);
  };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (justAddedClaimId && isSuccess) {
      timer = setTimeout(() => push(pathname), BACKLIGHT_DELAY);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [pathname, justAddedClaimId, isSuccess, push]);

  return (
    <Wrapper data-testid="RecentActivities-wrapper">
      <Box direction="row" align="center" margin={{ bottom: 'm' }} justify="between">
        <Text size="large" weight={700}>
          {t('Expenses')}
        </Text>

        {total > 5 ? (
          <Box width="control">
            <AppButton
              onClick={handleOpenList}
              buttonType="secondary"
              width="control"
              testId="RecentActivityButtonShowMore-id"
            >
              <Inscription style={{ whiteSpace: 'nowrap' }}>{t('View All Expenses')}</Inscription>
            </AppButton>
          </Box>
        ) : null}
      </Box>
      {isLoading && <Preloader />}
      {!isLoading && !data.length ? (
        <EmptyTransactionsListPlaceholder title="Expenses" boxStyle={{ paddingTop: 0 }} isDescriptionHidden />
      ) : null}
      {data?.length > 0 && (
        <>
          <Responsive sizes={[Responsive.Desktop, Responsive.Tablet]}>
            <TransactionsListHeader items={headers} />
          </Responsive>
          {data?.slice(0, 5)?.map((item: Transaction) => (
            <RecentActivity
              key={item.claimId}
              data={item}
              hasBacklight={item?.claimId === justAddedClaimId}
              onPick={() => push(ROUTES.EXPENSES_TRANSACTION_DETAILS_CLAIM(item?.claimId))}
            />
          ))}
        </>
      )}
    </Wrapper>
  );
};
export default RecentActivities;
